import React, { createContext, useState, useContext, useEffect, useCallback, ReactNode } from 'react';
import { AxiosError } from 'axios';
import { useLocation, useSearchParams } from 'react-router';
import { API_ENDPOINTS } from '@/constants';
import api from '@/utils/api';
import { useSnackbar } from './SnackbarContext';

interface AuthContextType {
    isAuthenticated: boolean;
    isLoading: boolean;
    error: string | null;
    role: string | null;
    userId: string | null;
    checkAuth: () => Promise<boolean>;
    authenticate: (jwt: string) => Promise<boolean>;
    logout: () => Promise<void>;
    fetchOriginalApiKey: () => Promise<void>;
}

interface AuthState {
    isAuthenticated: boolean;
    isLoading: boolean;
    error: string | null;
    role: string | null;
    userId: string | null;
}

interface ErrorResponse {
    error: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
    children: ReactNode;
}

/**
 * AuthProvider provides the authentication state to the app. It handles checking if the user is authenticated,
 * authenticating the user with a JWT token and logging out the user.
 *
 * @param children - The children of the AuthProvider
 * @returns {React.FC}
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { showSnackbar } = useSnackbar();
    const [authState, setAuthState] = useState<AuthState>({
        isAuthenticated: false,
        isLoading: true,
        error: null,
        role: null,
        userId: null,
    });

    // Fetch Original API key
    const fetchOriginalApiKey = useCallback(async (): Promise<void> => {
        try {
            await api.post(API_ENDPOINTS.AUTH_ORIGINAL_API_KEY);
        } catch (err) {
            const axiosError = err as AxiosError<ErrorResponse>;
            const errorMessage = axiosError.response?.data?.error || 'Failed to retrieve Original API key';

            showSnackbar(errorMessage, 'error');
        }
    }, [showSnackbar]);

    // Check if the user is authenticated
    const checkAuth = useCallback(async (): Promise<boolean> => {
        try {
            setAuthState((prev) => ({ ...prev, isLoading: true, error: null }));

            const response = await api.get(API_ENDPOINTS.AUTH_CHECK);

            if (response.status === 200) {
                setAuthState((prev) => ({
                    ...prev,
                    isAuthenticated: true,
                    isLoading: false,
                    role: response.data?.role ?? null,
                    userId: response.data?.userId ?? null,
                }));
                fetchOriginalApiKey();

                return true;
            }

            setAuthState((prev) => ({ ...prev, isAuthenticated: false, isLoading: false, role: null, userId: null }));

            return false;
        } catch (err) {
            const axiosError = err as AxiosError<ErrorResponse>;

            const errorMessage =
                axiosError.response?.status === 403 ? 'Please log in to continue.' : 'An error occurred while checking authentication.';

            setAuthState({
                isAuthenticated: false,
                isLoading: false,
                error: errorMessage,
                role: null,
                userId: null,
            });

            return false;
        }
    }, [fetchOriginalApiKey]);

    // Authenticate user with a JWT token
    const authenticate = useCallback(
        async (jwt: string): Promise<boolean> => {
            try {
                setAuthState((prev) => ({ ...prev, isLoading: true, error: null }));

                const response = await api.get(API_ENDPOINTS.AUTH, { params: { jwt } });

                if (response.status === 200) {
                    setAuthState((prev) => ({
                        ...prev,
                        isAuthenticated: true,
                        isLoading: false,
                        role: response.data?.role ?? null,
                        userId: response.data?.userId ?? null,
                    }));
                    fetchOriginalApiKey();

                    return true;
                }

                setAuthState((prev) => ({ ...prev, isAuthenticated: false, isLoading: false, role: null, userId: null }));

                return false;
            } catch (err) {
                const axiosError = err as AxiosError<ErrorResponse>;
                const errorMessage = axiosError.response?.data?.error || 'Error occurred during authentication.';

                setAuthState({
                    isAuthenticated: false,
                    isLoading: false,
                    error: errorMessage,
                    role: null,
                    userId: null,
                });

                return false;
            }
        },
        [fetchOriginalApiKey]
    );

    // Logout the user
    const logout = async (): Promise<void> => {
        try {
            setAuthState((prev) => ({ ...prev, isLoading: true }));
            await api.post(API_ENDPOINTS.AUTH_LOGOUT);
        } catch {
            throw new Error('Error logging out');
        } finally {
            setAuthState({
                isAuthenticated: false,
                isLoading: false,
                error: null,
                role: null,
                userId: null,
            });
        }
    };

    useEffect(() => {
        // Only run automatic auth check on initial mount
        const hasJwtParam = searchParams.get('jwt');
        const isAuthPage = location.pathname.startsWith('/auth');

        if (!isAuthPage && !hasJwtParam) {
            checkAuth();
        } else {
            // On auth pages, set loading to false to let AuthLogin handle authentication
            setAuthState((prev) => ({ ...prev, isLoading: false }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <AuthContext.Provider
            value={{
                isAuthenticated: authState.isAuthenticated,
                isLoading: authState.isLoading,
                error: authState.error,
                role: authState.role,
                userId: authState.userId,
                checkAuth,
                authenticate,
                logout,
                fetchOriginalApiKey,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);

    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }

    return context;
};
